/**
 * ComfyOutput.jsx
 * Page container for browsing media generated by ComfyUI.
 *
 * Merged from the old ComfyOutput (container) + ComfyOutputLayout (presentation).
 * Manages filter/sort state and action handlers, renders the header controls
 * and ComfyGallery directly.
 *
 * Electron-only: This page is only accessible when running in Electron.
 */

import { useEffect, useState, useCallback } from "react";
import { useDriverContextDispatch } from '../context/DriverContext.js';
import ComfyGallery from '../components/ComfyGallery';
import { rebuildDatabase } from '../services/comfyOutputService';
import electronBridge from '../utils/electronBridge';
import Logo from "../images/logo_light_grey.png";

export default function ComfyOutput() {
  // ─── State ───────────────────────────────────────────────────────────────
  const [category, setCategory] = useState("all");
  const [sortMethod, setSortMethod] = useState("newest");
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [isRebuildingDatabase, setIsRebuildingDatabase] = useState(false);
  const [columnClass, setColumnClass] = useState("cols-4");

  const driverContextDispatch = useDriverContextDispatch();

  // ─── Page Setup ──────────────────────────────────────────────────────────
  useEffect(() => {
    electronBridge.ipcRenderer?.send("pwa-log", "React - ComfyOutput Page");

    driverContextDispatch({
      type: 'setupHeader',
      payload: {
        header: {
          title: "ComfyUI Output",
          hideBackButton: true,
          hideSearchButton: true,
          hideSettingsButton: false
        }
      }
    });
  }, []);

  // ─── Handlers ────────────────────────────────────────────────────────────

  /**
   * Called by ComfyGallery whenever it receives new totalCount from the API.
   * This drives the "Total Items" display in the header.
   */
  const handleCountUpdate = useCallback((count) => {
    setTotalCount(count);
  }, []);

  /**
   * Rebuild the SQLite database by re-scanning the ComfyUI output directory.
   * After rebuild, ComfyGallery will re-fetch automatically because category/sortMethod
   * state is unchanged but the data behind it has changed.
   */
  const handleRebuildDatabase = async () => {
    if (isRebuildingDatabase) return;
    setIsRebuildingDatabase(true);
    setLoading(true);
    try {
      await rebuildDatabase();
    } catch (error) {
      console.error('[ComfyOutput] Error rebuilding database:', error);
    } finally {
      setIsRebuildingDatabase(false);
      setLoading(false);
    }
  };

  /**
   * Convert the column class string to a numeric column count for ComfyGallery.
   */
  const getColumnCount = () => {
    const map = { "cols-2": 2, "cols-3": 3, "cols-4": 4, "cols-5": 5, "cols-6": 6 };
    return map[columnClass] || 4;
  };

  // ─── Render ──────────────────────────────────────────────────────────────
  return (
    <div className="px-4 pb-4 relative w-full max-w-full">
      {/* ── Header Controls ── */}
      <h2 className="text-xl py-2 font-bold flex flex-row items-center w-full flex-wrap gap-2">
        <span>ComfyUI Output</span>
        <hr className="flex-grow border border-white/40 bg-white/40" />

        {/* Sort dropdown */}
        <select
          className="rounded-lg text-dark-blue border-0 p-2"
          value={sortMethod}
          onChange={(e) => setSortMethod(e.target.value)}
        >
          <option value="newest">Newest First</option>
          <option value="oldest">Oldest First</option>
        </select>

        {/* Category filter dropdown */}
        <select
          className="rounded-lg text-dark-blue border-0 p-2"
          value={category}
          onChange={(e) => setCategory(e.target.value)}
        >
          <option value="all">All</option>
          <option value="image">Images</option>
          <option value="video">Videos</option>
          <option value="audio">Audio</option>
          <option value="3d">3D Models</option>
        </select>

        <button
          className="py-2 px-4 border rounded bg-red-400 transition text-sm"
          onClick={handleRebuildDatabase}
          disabled={isRebuildingDatabase}
        >
          Rebuild Database
        </button>

        {/* Total count + column selector */}
        <div className="flex flex-row items-center gap-3 ml-auto">
          <span className="font-light text-sm">
            Total Items: <span className="font-bold">{totalCount}</span>
          </span>

          <select
            className="rounded-lg text-dark-blue border-0 p-2"
            value={columnClass}
            onChange={(e) => setColumnClass(e.target.value)}
          >
            <option value="cols-2">2 Columns</option>
            <option value="cols-3">3 Columns</option>
            <option value="cols-4">4 Columns</option>
            <option value="cols-5">5 Columns</option>
            <option value="cols-6">6 Columns</option>
          </select>
        </div>
      </h2>

      {/* ── Content ── */}
      {loading ? (
        <div className="w-2/3 mx-auto">
          <img src={Logo} alt="Loading" className="w-2/3 mx-auto animate-pulse" />
        </div>
      ) : (
        <ComfyGallery
          category={category}
          sortMethod={sortMethod}
          columnCount={getColumnCount()}
          onCountUpdate={handleCountUpdate}
        />
      )}
    </div>
  );
}
